import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { toastError } from "@/lib/errorToast";
import { supabase } from "@/integrations/supabase/client";
import { useUserIntentProfile } from "@/hooks/useUserIntentProfile";
import { useChatHistory, type ChatMessage, type ChatMessageAction } from "@/hooks/useChatHistory";
import { useCyclePlanning } from "@/hooks/useCyclePlanning";
import { useDynamicFatigue } from "@/hooks/useDynamicFatigue";
import { getWeekTypeConfig } from "@/types/weekTypes";
import type { PageContext } from "@/hooks/usePageContext";
import { type BrainErrorType, classifyBrainError } from "@/components/brain/BrainErrorState";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

export function useBrainEngine() {
  const [loading, setLoading] = useState(false);
  const [brainError, setBrainError] = useState<{ type: BrainErrorType; waitMinutes?: number } | null>(null);
  const [dbLoaded, setDbLoaded] = useState(false);
  const { profile } = useUserIntentProfile();
  const chatHistory = useChatHistory();
  const { currentWeekStatus } = useCyclePlanning();
  const { fatigue: dynamicFatigue } = useDynamicFatigue();

  // Load chat history from DB on mount
  useEffect(() => {
    if (dbLoaded) return;
    let cancelled = false;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data } = await supabase
          .from("chat_messages" as any)
          .select("role, content, action, created_at")
          .eq("user_id", session.user.id)
          .eq("thread_id", "default")
          .order("created_at", { ascending: true })
          .limit(50);

        if (!cancelled && data && (data as any[]).length > 0) {
          chatHistory.loadFromDb(data as any[]);
        }
      } catch {
        // Silent — fallback to localStorage
      } finally {
        if (!cancelled) setDbLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [dbLoaded]);

  const send = useCallback(async (query: string, pageContext: PageContext, mode: "dock" | "full" = "dock") => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setBrainError(null);

    chatHistory.addMessage({ role: "user", content: query.trim() });
    chatHistory.addMessage({ role: "assistant", content: "" });

    try {
      const body: Record<string, unknown> = { query: query.trim() };

      // Inject page context
      body.page_context = {
        route: pageContext.route,
        page: pageContext.page,
        guidance_hints: pageContext.guidance_hints,
        mode,
      };

      // Inject profile
      if (profile) {
        body.user_profile = {
          objectif_principal: profile.objectif_principal,
          objectifs_secondaires: profile.objectifs_secondaires || [],
          niveau_sportif: profile.niveau_sportif,
          age: profile.age,
          sexe: profile.sexe,
          jours_par_semaine: profile.jours_par_semaine,
          duree_par_session: profile.duree_par_session,
          blessures_connues: profile.blessures_connues || [],
          materiel_disponible: profile.materiel_disponible,
          lieu: profile.lieu,
          restrictions_medicales: profile.restrictions_medicales,
          exercices_aimes: profile.exercices_aimes || [],
          exercices_evites: profile.exercices_evites || [],
          priorite_actuelle: profile.priorite_actuelle,
        };
        const levelMap: Record<string, string> = {
          "Débutant": "debutant",
          "Intermédiaire": "intermediaire",
          "Avancé": "avance",
          "Athlète confirmé": "avance",
        };
        body.user_level = levelMap[profile.niveau_sportif] || "avance";
        body.onboarding_completed = profile.completed === true;
      }

      // Inject week type context
      if (currentWeekStatus) {
        const wtConfig = getWeekTypeConfig(currentWeekStatus.type);
        const effectiveFatigue = dynamicFatigue?.reliable
          ? dynamicFatigue.score
          : wtConfig.fatigueDefault;
        const effectiveRecovery = wtConfig.recoveryPriority || (effectiveFatigue >= 7);

        body.week_type_context = {
          week_type: currentWeekStatus.type,
          label: wtConfig.label,
          fatigue_level: effectiveFatigue,
          max_sport_hours: wtConfig.maxSportHours,
          recovery_priority: effectiveRecovery,
          cycle_position: currentWeekStatus.position,
          cycle_total: currentWeekStatus.total,
          is_override: currentWeekStatus.isOverride,
          ...(dynamicFatigue?.reliable && {
            fatigue_source: "checkins",
            fatigue_factors: dynamicFatigue.factors,
            fatigue_checkin_count: dynamicFatigue.checkinCount,
          }),
        };
      } else if (dynamicFatigue?.reliable) {
        body.week_type_context = {
          week_type: "libre",
          label: "Sans cycle",
          fatigue_level: dynamicFatigue.score,
          max_sport_hours: 10,
          recovery_priority: dynamicFatigue.score >= 7,
          fatigue_source: "checkins",
          fatigue_factors: dynamicFatigue.factors,
          fatigue_checkin_count: dynamicFatigue.checkinCount,
        };
      }

      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) {
        toastError("Connexion requise.", "Cerveau");
        setLoading(false);
        return;
      }

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/reason`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ text: "Erreur inconnue" }));
        const errMsg = err.text || err.error || `Erreur ${resp.status}`;
        const errorType = classifyBrainError(resp.status, null, false);
        const waitMinutes = resp.status === 429 ? (parseInt(resp.headers.get("retry-after") || "0") / 60 || undefined) : undefined;
        setBrainError({ type: errorType, waitMinutes: waitMinutes ? Math.ceil(waitMinutes) : undefined });
        chatHistory.updateLastAssistant(errMsg);
        setLoading(false);
        return;
      }

      const contentType = resp.headers.get("content-type") || "";

      if (contentType.includes("application/json")) {
        const data = await resp.json();
        // Handle new JSON format { text, action }
        const text = data.text || data.answer || "Pas de réponse.";
        chatHistory.updateLastAssistant(text, undefined, data.action || null);
        setLoading(false);
        return;
      }

      // Streaming SSE
      const reader = resp.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let accumulated = "";
      let metadata: ChatMessage["metadata"] | undefined;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let newlineIdx: number;
        while ((newlineIdx = buffer.indexOf("\n")) !== -1) {
          let line = buffer.slice(0, newlineIdx);
          buffer = buffer.slice(newlineIdx + 1);
          if (line.endsWith("\r")) line = line.slice(0, -1);
          if (line.startsWith(":") || line.trim() === "") continue;
          if (!line.startsWith("data: ")) continue;

          const jsonStr = line.slice(6).trim();
          if (jsonStr === "[DONE]") break;

          try {
            const parsed = JSON.parse(jsonStr);
            if (parsed.type === "metadata") {
              metadata = {
                total_facts_in_db: parsed.total_facts,
                eligible_after_level_filter: parsed.eligible_facts,
                fact_ids: parsed.fact_ids,
                profile_loaded: parsed.profile_loaded,
              };
              continue;
            }
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) {
              accumulated += content;
              chatHistory.updateLastAssistant(accumulated, metadata);
            }
          } catch {
            buffer = line + "\n" + buffer;
            break;
          }
        }
      }

      // Try to parse accumulated JSON response
      if (accumulated) {
        try {
          let jsonContent = accumulated.trim();
          if (jsonContent.startsWith("```")) {
            jsonContent = jsonContent.replace(/^```(?:json)?\s*/, "").replace(/\s*```$/, "");
          }
          const parsed = JSON.parse(jsonContent);
          if (parsed.text) {
            chatHistory.updateLastAssistant(parsed.text, metadata, parsed.action || null);
          } else {
            chatHistory.updateLastAssistant(accumulated, metadata);
          }
        } catch {
          // Not JSON — display raw text
          chatHistory.updateLastAssistant(accumulated, metadata);
        }
      }
    } catch (e) {
      console.error(e);
      const isTimeout = e instanceof DOMException && e.name === "AbortError";
      setBrainError({ type: classifyBrainError(undefined, null, isTimeout) });
    }

    setLoading(false);
  }, [loading, profile, chatHistory, currentWeekStatus, dynamicFatigue]);

  const clearHistory = useCallback(async () => {
    chatHistory.clearHistory();
    // Also clear DB messages
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        await supabase
          .from("chat_messages" as any)
          .delete()
          .eq("user_id", session.user.id)
          .eq("thread_id", "default");
      }
    } catch {
      // Silent
    }
  }, [chatHistory]);

  const addGuidedMessage = useCallback(
    (content: string, actions?: ChatMessageAction[]) => {
      chatHistory.addMessage({ role: "assistant", content, actions });
    },
    [chatHistory]
  );

  const clearBrainError = useCallback(() => setBrainError(null), []);

  return {
    messages: chatHistory.messages,
    loading,
    brainError,
    clearBrainError,
    send,
    clearHistory,
    addGuidedMessage,
    profile,
  };
}
